import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
  Box,
  Divider,
  CircularProgress,
  Radio,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AddIcon from '@mui/icons-material/Add';

const PinToDashboardDialog = ({
  open,
  onClose,
  onConfirm,
  dashboards,
  colors,
  isLoading = false,
  panelTitle = '',
}) => {
  const [selectedDashboard, setSelectedDashboard] = useState(null);
  const [isCreatingNew, setIsCreatingNew] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState('');

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedDashboard(null);
      setIsCreatingNew(false);
      setNewDashboardName('');
    }
  }, [open]);

  const handleSelectDashboard = (dashboardId) => {
    setSelectedDashboard(dashboardId);
    setIsCreatingNew(false);
  };

  const handleSelectCreateNew = () => {
    setSelectedDashboard(null);
    setIsCreatingNew(true);
  };

  const handleConfirm = () => {
    if (isCreatingNew && newDashboardName.trim()) {
      onConfirm(null, true, newDashboardName.trim());
    } else if (selectedDashboard) {
      onConfirm(selectedDashboard, false, null);
    }
  };

  const isConfirmDisabled = () => {
    if (isCreatingNew) {
      return !newDashboardName.trim();
    }
    return !selectedDashboard;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          border: `1px solid ${colors.border}`,
          borderRadius: 0,
        },
      }}
    >
      <DialogTitle sx={{ color: colors.textPrimary }}>
        Pin to Dashboard
        {panelTitle && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, mt: 0.5 }}>
            Panel: {panelTitle}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 4,
            }}
          >
            <CircularProgress size={32} sx={{ color: '#22c55e' }} />
          </Box>
        ) : (
          <>
            {/* Existing dashboards list */}
            {dashboards.length > 0 && (
              <>
                <Typography
                  variant="overline"
                  sx={{
                    color: colors.textMuted,
                    px: 2,
                    pt: 1,
                    display: 'block',
                  }}
                >
                  Existing Dashboards
                </Typography>
                <List sx={{ py: 0 }}>
                  {dashboards.map((dashboard) => (
                    <ListItem key={dashboard.dashboardId} disablePadding>
                      <ListItemButton
                        selected={selectedDashboard === dashboard.dashboardId}
                        onClick={() => handleSelectDashboard(dashboard.dashboardId)}
                        sx={{
                          '&.Mui-selected': {
                            bgcolor: 'rgba(34, 197, 94, 0.08)',
                            borderLeft: '3px solid #22c55e',
                          },
                          '&.Mui-selected:hover': {
                            bgcolor: 'rgba(34, 197, 94, 0.12)',
                          },
                          '&:hover': {
                            bgcolor: colors.hoverBg,
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Radio
                            checked={selectedDashboard === dashboard.dashboardId}
                            sx={{
                              color: colors.textMuted,
                              '&.Mui-checked': { color: '#22c55e' },
                            }}
                          />
                        </ListItemIcon>
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <DashboardIcon sx={{ color: colors.textSecondary }} />
                        </ListItemIcon>
                        <ListItemText
                          primary={dashboard.title || dashboard.name}
                          secondary={`${dashboard.panelCount || 0} panels`}
                          primaryTypographyProps={{
                            sx: { color: colors.textPrimary },
                          }}
                          secondaryTypographyProps={{
                            sx: { color: colors.textMuted },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
                <Divider sx={{ borderColor: colors.border }} />
              </>
            )}

            {/* Create new dashboard option */}
            <Typography
              variant="overline"
              sx={{
                color: colors.textMuted,
                px: 2,
                pt: 1,
                display: 'block',
              }}
            >
              Create New
            </Typography>
            <List sx={{ py: 0 }}>
              <ListItem disablePadding>
                <ListItemButton
                  selected={isCreatingNew}
                  onClick={handleSelectCreateNew}
                  sx={{
                    '&.Mui-selected': {
                      bgcolor: 'rgba(34, 197, 94, 0.08)',
                      borderLeft: '3px solid #22c55e',
                    },
                    '&.Mui-selected:hover': {
                      bgcolor: 'rgba(34, 197, 94, 0.12)',
                    },
                    '&:hover': {
                      bgcolor: colors.hoverBg,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Radio
                      checked={isCreatingNew}
                      sx={{
                        color: colors.textMuted,
                        '&.Mui-checked': { color: '#22c55e' },
                      }}
                    />
                  </ListItemIcon>
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <AddIcon sx={{ color: '#22c55e' }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Create New Dashboard"
                    primaryTypographyProps={{
                      sx: { color: '#22c55e', fontWeight: 500 },
                    }}
                  />
                </ListItemButton>
              </ListItem>
            </List>

            {/* New dashboard name field */}
            {isCreatingNew && (
              <Box sx={{ px: 2, pb: 2 }}>
                <TextField
                  autoFocus
                  fullWidth
                  size="small"
                  label="Dashboard Name"
                  placeholder="Enter dashboard name..."
                  value={newDashboardName}
                  onChange={(e) => setNewDashboardName(e.target.value)}
                  sx={{
                    mt: 1,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      bgcolor: colors.background,
                      '& fieldset': {
                        borderColor: colors.borderLight,
                      },
                      '&:hover fieldset': {
                        borderColor: '#22c55e',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#22c55e',
                      },
                    },
                    '& .MuiInputBase-input': {
                      color: colors.textPrimary,
                    },
                    '& .MuiInputLabel-root': {
                      color: colors.textSecondary,
                    },
                  }}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 2, borderTop: `1px solid ${colors.border}` }}>
        <Button
          onClick={onClose}
          sx={{
            color: colors.textSecondary,
            borderRadius: 0,
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={isConfirmDisabled() || isLoading}
          sx={{
            bgcolor: '#22c55e',
            color: colors.background,
            borderRadius: 0,
            '&:hover': {
              bgcolor: '#16a34a',
            },
            '&.Mui-disabled': {
              bgcolor: colors.surfaceElevated,
              color: colors.textMuted,
            },
          }}
        >
          Pin to Dashboard
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PinToDashboardDialog;
